import {createAction, createActionGroup, emptyProps, props} from '@ngrx/store';
import {ILoginStepProgress, IUser} from '../models';
import {AuthPersistenceType} from '../models/auth-persistence.enum';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';

//#region GLOBAL
/**
 * Sets a dynamic property and its value on the user state.
 * This is useful for updating a specific property without mutating the entire user object.
 *
 * @param property - The name of the property to update.
 * @param value - The new value for the specified property.
 */
export const setPropertyOnUserState = createAction(
  '[USER][Global] Set Property on User State',
  props<{
    property: string;
    value: unknown;
  }>(),
);

/**
 * Clears the user-related state from the global store.
 *
 * This action is typically dispatched when a user logs out
 * or when resetting the application state.
 *
 * @constant
 */
export const clearUserState = createAction(
  '[USER][Global] Clear User State',
);

/**
 * Action to set the persistence mode for Angular Fire Authentication.
 *
 * This action configures how Firebase stores the user's authentication state:
 *
 * - `'local'`: Persists the state even after the browser is closed (stored in `localStorage`).
 * - `'session'`: Persists only for the current browser tab or session (stored in `sessionStorage`).
 * - `'none'`: Does not persist the authentication state at all.
 *
 * This configuration is useful for controlling the longevity of the user session
 * according to application security needs.
 *
 * @action
 * @param {AuthPersistenceType} persistence - The persistence mode for Firebase Auth.
 */
export const setAngularFireAuthPersistence = createAction(
  '[USER][Global] Set Angular Fire Auth Persistence',
  props<{ persistence: AuthPersistenceType }>(),
);

/**
 * Action to set or update the user's login step progress metadata.
 *
 * This action is used to track the overall progress of the login flow,
 * including the total number of steps, the current step the user is on,
 * and the percent completed.
 *
 * @example
 * dispatch(setLoginProgress({
 *   progress: { totalSteps: 4, currentStep: 2, percentComplete: 50 }
 * }));
 *
 * @see ILoginStepProgress
 */
export const setLoginProgress = createAction(
  '[USER][Global] Set Login Progress',
  props<{ progress: ILoginStepProgress }>(),
);

/**
 * Action to set whether the current login was initiated manually by the user.
 *
 * This flag is used to determine if domain change modal should be shown.
 *
 * @example
 * dispatch(setManualLoginFlag({ isManualLogin: true }));
 */
export const setManualLoginFlag = createAction(
  '[USER][Global] Set Manual Login Flag',
  props<{ isManualLogin: boolean }>(),
);

/**
 * Action to set the visibility of the global modal container.
 *
 * @example
 * dispatch(setGlobalModalVisibility({
 *   visibility: false,
 *   modalType: ModalType.SignOut
 * }));
 *
 * @see ModalType
 *
 * @action
 * @param {boolean} visibility the visibility of the global modal container.
 * @param {string}  modalType {@link ModalType}  which Modal you want to display inside the component
 */
export const setGlobalModalVisibility = createAction(
  '[USER][Global] Set Global Modal Visibility',
  props<{
    visibility: boolean,
    modalType: string[],
  }>(),
);

//#endregion

//#region LOGIN / LOGOUT USER
export const loginUser = createAction(
  '[USER][User] Login User',
  props<{ userEmail: string; isManualLogin?: boolean }>(),
);

export const loginUserSuccess = createAction(
  '[USER][User] Login User Success',
  props<{ user: IUser; isManualLogin?: boolean }>(),
);

export const logOutUser = createAction(
  '[USER][User] Log Out User',
);

export const logOutUserSuccess = createAction(
  '[USER][User] Log Out User Success',
);
//#endregion

//#region [User | User Profile] Update User Profile
export const updateUserProfileDetails = createActionGroup({
  source: 'User | User Profile',
  events: {
    ['Update User Profile']: props<{ data: Partial<IUser> }>(),
    ['Update User Profile Success']: props<{ data: Partial<IUser> }>(),
    ['Update User Profile Failure']: props<{ error: IGlobalError }>(),
  },
});

//#endregion

//#region RESET USER PASSWORD
/**
 * Initiates the password reset process for a user.
 *
 * Dispatch this action to request a password reset email.
 *
 * @param {string} userEmail - The email address of the user requesting a password reset.
 */
export const resetUserPassword = createAction(
  '[USER][User] Reset User Password',
  props<{ userEmail: string }>(),
);

/**
 * Dispatched when the password reset request completes successfully.
 *
 * This action can be used to show a success message or navigate the user.
 */
export const resetUserPasswordSuccess = createAction(
  '[USER][User] Reset User Password Success',
);

/**
 * Dispatched when the password reset request fails.
 */
export const resetUserPasswordFailure = createAction(
  '[USER][User] Reset User Password Failure',
);
//#endregion

//#region [User | User Password] Change User Password
/**
 * Action group for changing the user's password.
 * Requires current password for authentication and new password to set.
 */
export const changeUserPassword = createActionGroup({
  source: 'User | User Password',
  events: {
    ['Change Password']: props<{ currentPassword: string; newPassword: string }>(),
    ['Change Password Success']: emptyProps(),
    ['Change Password Failure']: props<{ error: IGlobalError }>(),
  },
});
//#endregion

//#region GET USER DETAILS BY USER ID
/**
 * Action to retrieve user details by a given user ID.
 *
 * This action is typically dispatched after login or when user details
 * need to be independently fetched based on the user ID.
 *
 * @param {Object} props - The payload for the action.
 * @param {string} props.userId - The ID of the user whose details are being requested.
 * @param {boolean} props.isLoggingIn - Flag indicating whether the call is part of the login flow.
 */
export const getUserDetailsByUserId = createAction(
  '[USER][User] Get User Details By User Id',
  props<{ userId: string, isLoggingIn: boolean }>(),
);

/**
 * Action dispatched upon successful retrieval of user details by user ID.
 *
 * @param {IUser} user - The user object fetched by user ID if not found.
 */
export const getUserDetailsByUserIdSuccess = createAction(
  '[USER][User] Get User Details By User Id Success',
  props<{ user: IUser }>(),
);
//#endregion
